import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Download, AlertTriangle, Zap, Info, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SummaryPage = ({ result, lang, tr, onReset }) => {
  const { project, score, summary_en, summary_fr } = result;
  const summary = lang === 'fr' ? summary_fr : summary_en;
  
  const getMaturityBadgeStyle = () => {
    if (score.maturity_level === 'EARLY') return 'bg-amber-100 text-amber-800 border-amber-300';
    if (score.maturity_level === 'MID') return 'bg-blue-100 text-blue-800 border-blue-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const getScoreColor = () => {
    if (score.total_score < 50) return 'text-amber-600';
    if (score.total_score < 75) return 'text-blue-600';
    return 'text-green-600';
  };
  
  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/projects/${project.id}/pdf?lang=${lang}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = `project_${project.id.substring(0,8)}_${lang}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error('PDF download failed'); }
  };
  
  const downloadJson = () => {
    const uri = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(result, null, 2));
    const link = document.createElement('a'); link.href = uri; link.download = `project_${project.id.substring(0,8)}.json`;
    document.body.appendChild(link); link.click(); link.remove();
  };
  
  return (
    <div className="min-h-screen maritime-gradient py-8 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-6">
        
        {/* Header with Score */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#0B2A4A] font-['Tomorrow'] mb-4">{tr.summary.title}</h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className={`px-6 py-3 rounded-xl border-2 ${getMaturityBadgeStyle()}`}>
              <p className="text-sm font-medium opacity-80">{tr.summary.score}</p>
              <p className={`text-3xl font-bold ${getScoreColor()}`} data-testid="project-score-badge">{score.total_score}/100</p>
            </div>
            <div className={`px-6 py-3 rounded-xl border-2 ${getMaturityBadgeStyle()}`}>
              <p className="text-sm font-medium opacity-80">{tr.summary.maturity}</p>
              <p className="text-lg font-bold">{tr.maturityLevels[score.maturity_level]}</p>
            </div>
          </div>
        </div>

        {/* Recommended Next Step - Prominent */}
        <Card className="border-[#0B2A4A] border-2 bg-[#0B2A4A]/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[#0B2A4A]">
              <CheckCircle2 className="w-5 h-5" />
              {tr.summary.recommendedAction}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium text-[#0B2A4A]" data-testid="recommended-action-badge">
              {tr.actions[score.recommended_action] || score.recommended_action}
            </p>
          </CardContent>
        </Card>

        {/* Attention Points / Uncertainty Flags */}
        {score.flags && score.flags.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                {tr.summary.attentionPoints}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {score.flags.map((f,i) => (
                  <li key={i} className="flex items-start gap-2 text-amber-700" data-testid={`attention-flag-${i}`}>
                    <span className="mt-1">•</span>
                    <span>{f.replace(/_/g,' ')}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Energy Estimation */}
        {score.energy_estimate && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#0B2A4A]" />
                {tr.summary.energyEstimate}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center mb-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-[#0B2A4A]" data-testid="energy-hours">~{score.energy_estimate.daily_operation_hours}h</p>
                  <p className="text-sm text-muted-foreground">{tr.summary.hoursDay}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-[#0B2A4A]" data-testid="energy-power">~{score.energy_estimate.estimated_power_kw} kW</p>
                  <p className="text-sm text-muted-foreground">{tr.summary.estPower}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-[#0B2A4A]" data-testid="energy-kwh">~{score.energy_estimate.daily_energy_kwh_estimate} kWh</p>
                  <p className="text-sm text-muted-foreground">{tr.summary.energyDay}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center italic">{score.energy_estimate.note}</p>
            </CardContent>
          </Card>
        )}

        {/* Technical Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>{tr.summary.techSummary}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-lg overflow-auto max-h-96" data-testid="technical-summary">{summary}</pre>
          </CardContent>
        </Card>

        {/* Assumptions & Limitations */}
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-slate-700 text-base">
              <Info className="w-5 h-5" />
              {tr.summary.assumptions}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 leading-relaxed">
              {tr.summary.assumptionsText}
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center pt-4">
          <Button onClick={downloadPdf} className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />{tr.summary.downloadPdf}
          </Button>
          <Button variant="outline" onClick={downloadJson} data-testid="download-json-btn">
            <Download className="w-4 h-4 mr-2" />{tr.summary.downloadJson}
          </Button>
          <Button variant="secondary" onClick={onReset} data-testid="new-project-btn">
            {tr.summary.newProject}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
